import { useId } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Icon } from "./Icon";

/**
 * The card every dashboard section sits in. radius-xl per DS rule 8, one-pixel
 * border, clipped so grouped rows meet the edge cleanly.
 */
export function Panel({ children, labelledBy }: { children: ReactNode; labelledBy?: string }) {
  return (
    <section
      aria-labelledby={labelledBy}
      className="overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--bg)]"
    >
      {children}
    </section>
  );
}

/** Title, optional count, then anything trailing pushed to the right. */
export function PanelHead({ id, title, count, children }: { id?: string; title: string; count?: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex min-h-[44px] items-center gap-2 border-b border-[var(--border)] px-4">
      <h2 id={id} className="text-sm font-semibold">
        {title}
      </h2>
      {count !== undefined && <span className="font-medium text-[var(--text-3)]">{count}</span>}
      <span className="flex-1" />
      {children}
    </div>
  );
}

/**
 * A sticky-looking divider naming the group of rows beneath it. `tone` is the
 * urgency of its contents — overdue reads in error colours so a late group is
 * visible without reading the dates.
 */
/** The group heading's explanation bubble — the shape StatusLabel uses, so a
 *  status reads the same whether it heads a group or labels a row. */
function GroupHint({ id, text }: { id: string; text: string }) {
  return (
    <span
      id={id}
      role="tooltip"
      className="pointer-events-none invisible absolute bottom-full left-3 z-30 mb-1.5 w-max max-w-[15rem] whitespace-normal rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-normal leading-4 text-white opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100"
    >
      {text}
    </span>
  );
}

export function GroupHeader({
  label,
  count,
  tone = "neutral",
  hint,
  collapse,
}: {
  label: string;
  count?: number;
  tone?: "overdue" | "today" | "neutral";
  /** Hover/focus text explaining what the group means. The request tables
   *  carry the status definitions here, where the Status column used to. */
  hint?: string;
  /** Makes the header a toggle with a chevron: `controls` is the id of the rows it hides. */
  collapse?: { collapsed: boolean; onToggle: () => void; controls: string };
}) {
  const toneClass =
    tone === "overdue" ? "text-error-700" : tone === "today" ? "text-warning-700" : "text-[var(--text-2)]";
  const countClass = tone === "overdue" ? "text-error-600" : "text-[var(--text-3)]";
  const tipId = useId();
  const bar = `flex h-8 w-full items-center gap-2 border-b border-[var(--border)] bg-gray-100 px-3 text-xs font-medium ${toneClass}`;

  if (!collapse) {
    return (
      <div className={bar}>
        <span>{label}</span>
        {count !== undefined && <span className={countClass}>{count}</span>}
        {hint && <GroupHint id={tipId} text={hint} />}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={collapse.onToggle}
      aria-expanded={!collapse.collapsed}
      aria-controls={collapse.controls}
      aria-label={count === undefined ? label : `${label}, ${count} ${count === 1 ? "request" : "requests"}`}
      aria-describedby={hint ? tipId : undefined}
      className={`${bar} group relative text-left transition-colors hover:bg-gray-200 focus-visible:focus-ring`}
    >
      <Icon name="chevron-right" className={`flex-none text-[var(--text-3)] transition-transform ${collapse.collapsed ? "" : "rotate-90"}`} />
      <span>{label}</span>
      {count !== undefined && <span className={countClass}>{count}</span>}
      {hint && <GroupHint id={tipId} text={hint} />}
    </button>
  );
}

/** Trailing action in a panel head — a Link when `to` is given, else a button. */
export function PanelAction({ children, to, onClick }: { children: ReactNode; to?: string; onClick?: () => void }) {
  const cls =
    "rounded-sm px-2 py-1 text-xs font-medium text-[var(--text-2)] transition-colors hover:bg-gray-100 hover:text-[var(--text)] focus-visible:focus-ring";
  if (to) {
    return (
      <Link to={to} className={cls}>
        {children}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      {children}
    </button>
  );
}
